import { useDispatch, useSelector } from 'react-redux';
import {
	useGetRolesQuery,
	useGetStagesQuery,
	useGetStatesQuery,
	useGetRecruitingManagersQuery,
	useGetAllCompaniesQuery,
	useGetAllExternalManagersQuery,
} from '../store/apiSlices/recruitApiSlice';
import { ListFilterModel } from '../models/listFilter.model';
import { Suspense, useEffect, useState } from 'react';
import filter from '../assets/images/filter.svg';
import MultiSelectDropDown from './MultiselectDropdown/MultiselectDropdown';
import Dropdown from './Dropdown';
import { DropdownModel } from '../models/dropdown.model';
import { setListQueryFilters } from '../store/reducers/listFilterReducer';
import { Formik } from 'formik';
import close from '../assets/icons/close.svg';
import { RootState } from '../store/store';

interface listFilterProps {
	onCancel: () => void;
}
const ListFilter = ({ onCancel }: listFilterProps) => {
	const dispatch = useDispatch();
	const filters = useSelector((store: RootState) => store.listFilter);
	const userInfo = useSelector((store: RootState) => store.user);
	const roles = useGetRolesQuery(null);
	const stages = useGetStagesQuery(null);
	const allExternalRecruitingManagers = useGetAllExternalManagersQuery(null, {
		refetchOnMountOrArgChange: true,
	});
	const recruitingManagers = useGetRecruitingManagersQuery({
		userEmail: userInfo.user_email,
		isSysAdmin: !userInfo.roles.adminAppAccessedUserListInclusion,
	});
	const allCompanies = useGetAllCompaniesQuery(null, {
		refetchOnMountOrArgChange: true,
	});
	const states = useGetStatesQuery(null);

	const handleFilterRoles = () => {
		return (
			roles?.currentData?.value?.filter((e) => {
				const role = filters?.roles?.includes(e.id);
				return role ? e : null;
			})?.[0]?.id || null
		);
	};

	const [updatedRoles, setUpdatedRoles] = useState<string | null>(
		handleFilterRoles(),
	);
	const [listFilters, setListFilters] = useState<ListFilterModel>(filters);

	useEffect(() => {
		if (!filters.roles || !roles?.currentData?.value?.length) return;
		const newRoles = roles.currentData.value.filter((e) => {
			const role = filters?.roles?.includes(e.id);
			return role ? e : null;
		});
		setUpdatedRoles(newRoles?.[0]?.id || null);
		setListFilters({ ...filters });
	}, [filters, roles.currentData]);

	const onRoleSelect = (e: DropdownModel) => {
		setListFilters({
			...listFilters,
			roles: e.id.toString(),
		});
		setUpdatedRoles(e.id);
	};

	const onStageSelect = (e: string[]) => {
		setListFilters({
			...listFilters,
			stages: e,
		});
	};

	const onAllExternalManagers = (e: string[]) => {
		setListFilters({
			...listFilters,
			all_external_managers: e,
		});
	};

	const onRecruitingManagerSelect = (e: string[]) => {
		setListFilters({
			...listFilters,
			recruiting_managers: e,
		});
	};

	const onAllCompaniesSelect = (e: string[]) => {
		setListFilters({
			...listFilters,
			all_companies: e,
		});
	};

	const onStateSelect = (e: string[]) => {
		setListFilters({
			...listFilters,
			states: e,
		});
	};

	const handleApply = () => {
		dispatch(
			setListQueryFilters({
				...listFilters,
				isFilterRequired: true,
			}),
		);
		onCancel();
	};

	const handleCancel = () => {
		onCancel();
	};

	const handleClear = () => {
		setUpdatedRoles(null);
		setListFilters({
			roles: null,
			stages: [],
			all_external_managers: [],
			recruiting_managers: [],
			all_companies: [],
			states: [],
			isFilterRequired: false,
			isRecruitingManagersRequired: filters.isRecruitingManagersRequired,
		});
	};

	return (
		<Formik
			initialValues={{
				roles: updatedRoles,
				stages: listFilters.stages,
				all_external_managers: listFilters.all_external_managers,
				recruiting_managers: listFilters.recruiting_managers,
				all_companies: listFilters.all_companies,
				states: listFilters.states,
			}}
			enableReinitialize
			onSubmit={() => handleApply()}
		>
			{() => (
				<div className="flex flex-col h-full">
					<div className="flex justify-between items-center">
						<div className="flex gap-2 px-2 py-3 mx-3 items-center bg-white">
							<img
								src={filter}
								className="w-4 h-4"
								alt="filter"
							></img>
							<div className="text-rcrm-title font-semibold text-3xl">
								Filters
							</div>
						</div>
						<img
							src={close}
							className="w-3 h-3 mx-3 cursor-pointer"
							alt=""
							onClick={() => handleCancel()}
						/>
					</div>
					<div className="flex flex-col px-10 justify-between bg-rcrm-primary-bg h-full">
						<div>
							<div className="font-semibold mt-4">
								<div className="mt-1">
									<Suspense fallback="loading...">
										<Dropdown
											list={
												roles.currentData?.value || []
											}
											label="Role"
											name="roles"
											isRequired={false}
											placeholder="Select Role"
											dropdownContainer="bg-white"
											selectedItemClassName="text-gray-700 text-sm font-semibold"
											listItemClassName="text-gray-700 text-sm font-semibold"
											onChangeSelection={onRoleSelect}
										/>
									</Suspense>
								</div>
							</div>
							<div className="text-rcrm-title font-semibold mt-4">
								Stages
								<div className="mt-1">
									<Suspense fallback="loading...">
										{stages.isSuccess ? (
											<MultiSelectDropDown
												name="stages"
												options={
													stages.isSuccess
														? (stages.currentData
																?.value as DropdownModel[])
														: []
												}
												onOptionChange={onStageSelect}
												_selectedFilters={
													listFilters?.stages?.filter(
														(x: string) => x != '',
													) ?? []
												}
											/>
										) : null}
									</Suspense>
								</div>
							</div>
							<div className="text-rcrm-title font-semibold mt-4">
								Outside Recruiters
								<div className="mt-1">
									<Suspense fallback="loading...">
										{allExternalRecruitingManagers.isSuccess ? (
											<MultiSelectDropDown
												name="all_external_managers"
												options={
													allExternalRecruitingManagers.isSuccess
														? (allExternalRecruitingManagers
																.currentData
																?.value as DropdownModel[])
														: []
												}
												onOptionChange={
													onAllExternalManagers
												}
												_selectedFilters={
													listFilters?.all_external_managers?.filter(
														(x: string) => x != '',
													) ?? []
												}
											/>
										) : null}
									</Suspense>
								</div>
							</div>
							{listFilters.isRecruitingManagersRequired ? (
								<div className="text-rcrm-title font-semibold mt-4">
									Recruiting Managers
									<div className="mt-1">
										<Suspense fallback="loading...">
											{recruitingManagers.isSuccess ? (
												<MultiSelectDropDown
													name="recruiting_managers"
													options={
														recruitingManagers.isSuccess
															? (recruitingManagers
																	.currentData
																	?.value as DropdownModel[])
															: []
													}
													onOptionChange={
														onRecruitingManagerSelect
													}
													_selectedFilters={
														listFilters?.recruiting_managers?.filter(
															(x: string) =>
																x != '',
														) ?? []
													}
												/>
											) : null}
										</Suspense>
									</div>
								</div>
							) : null}
							<div className="text-rcrm-title font-semibold mt-4">
								Current Companies
								<div className="mt-1">
									<Suspense fallback="loading...">
										{allCompanies.isSuccess ? (
											<MultiSelectDropDown
												name="all_companies"
												options={
													allCompanies.isSuccess
														? (allCompanies
																.currentData
																?.value as DropdownModel[])
														: []
												}
												onOptionChange={
													onAllCompaniesSelect
												}
												_selectedFilters={
													listFilters?.all_companies?.filter(
														(x: string) => x != '',
													) ?? []
												}
											/>
										) : null}
									</Suspense>
								</div>
							</div>{' '}
							<div className="text-rcrm-title font-semibold mt-4">
								States
								<div className="mt-1">
									<Suspense fallback="loading...">
										{states.isSuccess ? (
											<MultiSelectDropDown
												name="states"
												options={
													states.isSuccess
														? (states.currentData
																?.value as DropdownModel[])
														: []
												}
												onOptionChange={onStateSelect}
												_selectedFilters={
													listFilters?.states?.filter(
														(x: string) => x != '',
													) ?? []
												}
											/>
										) : null}
									</Suspense>
								</div>
							</div>
						</div>
						<div className="flex justify-between mb-5">
							<button
								onClick={() => handleClear()}
								className="bg-white font-semibold  p-3"
							>
								Clear All
							</button>
							<button
								type="submit"
								onClick={() => handleApply()}
								className="bg-rcrm-primary-blue text-white font-semibold  p-3"
							>
								Apply Filter
							</button>
						</div>
					</div>
				</div>
			)}
		</Formik>
	);
};

export default ListFilter;
